import { HTMLAttributes } from 'react'
import Link from 'next/link'

import CtaButton from '@/components/base/cta-button'
import Motion from '@/components/base/motion'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { footerRevealAnimation } from '@/components/sections/animations/footer'
import Newsletter from '@/components/sections/newsletter'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLDivElement> & {
  footer?: any
}

const Footer = ({ footer, className, ...props }: FooterProps) => {
  const { strings, menu } = useStore()

  const socialMenu = () => (
    <nav aria-label={strings?.labels?.social_navigation || 'Social'}>
      <ul className="flex gap-spacing-sm md:translate-y-[.7rem]">
        {menu?.social_menu?.menu?.map((item, index) => (
          <li key={`${item.id}-${index}`}>
            <Link
              href={item.url || '#'}
              target={item.target || '_self'}
              rel="noopener noreferrer"
              scroll={false}
              aria-label={item.title ? `Seguici su ${item.title}` : undefined}
              className="group"
            >
              <SVGIcon
                symbol={item.title.toLowerCase() || 'facebook'}
                className="size-[24px] transition-opacity duration-300 group-hover:opacity-50"
              />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )

  return (
    <Motion animation={footerRevealAnimation}>
      {(motionRef) => (
        <div ref={motionRef}>
          {footer?.newsletter ? <Newsletter data-motion-newsletter /> : null}

          <footer
            className={cn('relative', className)}
            {...props}
            data-motion-footer
          >
            <div
              className={cn(
                'flex flex-wrap justify-between border-t-[2px] px-spacing-md',
                'border-border-primary-light/30 dark:border-border-primary-dark/30',
                'gap-spacing-2xl lg:gap-spacing-lg',
                'py-spacing-lg lg:py-spacing-2xl'
              )}
            >
              {/* Column 1 */}
              <div
                className={cn(
                  'flex w-[100%] flex-col justify-between lg:w-[20vw]'
                )}
              >
                <Link href="/" scroll={false} className={cn('')}>
                  <span className="sr-only">{strings?.brand_name}</span>
                  <SVGIcon
                    symbol="logo"
                    className="h-[16px] w-[116px] fill-current"
                  />
                </Link>
                <div className="hidden py-padding-sm text-text-secondary-light lg:block dark:text-text-secondary-dark">
                  <Typography variant="small" paragraph>
                    {strings?.footer?.text || ''}
                  </Typography>
                </div>
                <div className="hidden lg:block">{socialMenu()}</div>
              </div>

              {/* Column 2 */}
              <div
                className={cn(
                  'flex w-[100%] flex-col justify-start lg:w-[20vw]',
                  'gap-[30px] lg:gap-spacing-xs'
                )}
              >
                <nav
                  aria-label={menu?.main_menu_02?.acf?.title || 'Collection'}
                  className={cn(
                    'flex flex-col gap-spacing-xs',
                    'gap-spacing-xs'
                  )}
                >
                  <Typography variant="small" className="condensed">
                    {menu?.main_menu_02?.acf?.title || 'Collection'}
                  </Typography>
                  <ul className="flex flex-col gap-[14px]">
                    {menu?.main_menu_02?.menu?.map((item, index) => (
                      <li key={`${item.id}-${index}`}>
                        <CtaButton
                          variant="link"
                          typography="h6"
                          iconSize="24px"
                          href={item.url || '#'}
                          target={item.target || '_self'}
                        >
                          {item.title}
                        </CtaButton>
                      </li>
                    ))}
                  </ul>
                </nav>
                <hr className="h-[2px] w-full border-0 bg-border-secondary-light dark:bg-border-secondary-dark" />
                <nav
                  aria-label={
                    menu?.main_menu_01?.acf?.title || 'Menu principale'
                  }
                  className="flex flex-col pt-[3px]"
                >
                  <ul className="flex flex-col gap-spacing-xs">
                    {menu?.main_menu_01?.menu?.map((item, index) => (
                      <li key={`${item.id}-${index}`} className="flex">
                        <CtaButton
                          href={item.url || '#'}
                          target={item.target || '_self'}
                          className="flex"
                          variant="link"
                          typography="large"
                          endIcon={null}
                        >
                          {item.title}
                        </CtaButton>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>

              {/* Column 3 */}
              <div
                className={cn(
                  'flex w-[100%] flex-col justify-between lg:w-[20vw]',
                  'gap-[30px] lg:gap-spacing-xs'
                )}
              >
                <nav
                  aria-label={menu?.main_menu_04?.acf?.title || 'Company'}
                  className={cn('flex flex-col', 'gap-spacing-xs')}
                >
                  <Typography variant="small" className="condensed">
                    {menu?.main_menu_04?.acf?.title || 'Company'}
                  </Typography>
                  <ul className="flex flex-col gap-spacing-xs">
                    {menu?.main_menu_04?.menu?.map((item, index) => (
                      <li key={`${item.id}-${index}`}>
                        <CtaButton
                          href={item.url || '#'}
                          target={item.target || '_self'}
                          className="flex"
                          variant="link"
                          typography="large"
                          endIcon={null}
                        >
                          {item.title}
                        </CtaButton>
                      </li>
                    ))}
                  </ul>
                </nav>
                <nav
                  aria-label={menu?.main_menu_03?.acf?.title || 'Professionals'}
                  className={cn('flex flex-col', 'gap-spacing-xs')}
                >
                  <Typography variant="small" className="condensed">
                    {menu?.main_menu_03?.acf?.title || 'Professionals'}
                  </Typography>
                  <ul className="flex flex-col gap-spacing-xs">
                    {menu?.main_menu_03?.menu?.map((item, index) => (
                      <li key={`${item.id}-${index}`}>
                        <CtaButton
                          href={item.url || '#'}
                          target={item.target || '_self'}
                          className="flex"
                          variant="link"
                          typography="large"
                          endIcon={null}
                        >
                          {item.title}
                        </CtaButton>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>

              {/* Column 4 */}
              <div
                className={cn(
                  'flex w-[100%] flex-col justify-between lg:w-[20vw]',
                  'gap-[30px] lg:gap-spacing-xs'
                )}
              >
                <nav
                  aria-label={menu?.legals_menu?.acf?.title || 'Legal'}
                  className={cn('flex flex-col', 'gap-spacing-xs')}
                >
                  <Typography variant="small" className="condensed">
                    {menu?.legals_menu?.acf?.title || 'Legal'}
                  </Typography>
                  <ul className="flex flex-col gap-spacing-xs">
                    {menu?.legals_menu?.menu?.map((item, index) => (
                      <li key={`${item.id}-${index}`}>
                        <CtaButton
                          href={item.url || '#'}
                          target={item.target || '_self'}
                          className="flex"
                          variant="link"
                          typography="large"
                          endIcon={null}
                        >
                          {item.title}
                        </CtaButton>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>

              {/* Footer end */}
              <div className="-mt-spacing-lg flex w-full flex-col gap-spacing-sm lg:hidden">
                <div className="flex">{socialMenu()}</div>
                <div className="py-padding-sm text-text-secondary-light dark:text-text-secondary-dark">
                  <Typography variant="small" paragraph>
                    {strings?.footer?.text || ''}
                  </Typography>
                </div>
              </div>
            </div>
          </footer>
        </div>
      )}
    </Motion>
  )
}

export default Footer
